import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { RestapiService } from '@app/services/restapi.service';
import { TemplatesCacheService } from '@main-application/shared/template-editor-dialog/services/templates-cache.service';
import { RestInspectionTemplateCategoryModel, RestTemplateModel } from '@template/models/rest-template-model.interface';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

@Injectable({
  providedIn: 'root',
})
export class TemplateManagerService {
  constructor(
    private restApiService: RestapiService,
    private snackbarService: SnackbarService,
    private templatesCacheService: TemplatesCacheService
  ) {}

  getGlobalTemplates(): Observable<RestTemplateModel[]> {
    return this.restApiService.getData<RestTemplateModel[]>(`inspections/template/global`);
  }

  getViewedTemplates(): Observable<number[]> {
    return this.restApiService.getData<number[]>(`inspections/template/viewed`);
  }

  markTemplatesAsViewed(templateIds: number[]) {
    return this.restApiService.post(`inspections/template/markAsViewed`, templateIds);
  }

  markAsNew(templateId: number) {
    return this.restApiService
      .post(`inspections/template/global/${templateId}/flagAsNew`)
      .pipe(catchError(err => this.handleError(err, SnackbarErrorMessage.UpdatingTemplate)));
  }

  getTemplate(templateId: number) {
    return this.restApiService.getData<RestTemplateModel>(`inspections/template/global/${templateId}`);
  }

  installTemplate(templateId: number): Observable<RestTemplateModel> {
    return this.restApiService
      .post<RestTemplateModel>(`inspections/template/installGlobalTemplate/${templateId}`)
      .pipe(catchError(err => this.handleError(err, SnackbarErrorMessage.InstallingTemplate)));
  }

  removeTemplate(template: RestTemplateModel) {
    return this.restApiService.delete(`inspections/template/global/${template.id}`).pipe(
      catchError(err => {
        if (err?.status === 403) {
          this.snackbarService.error(SnackbarErrorMessage.TemplateEditPermissionRequired);
          return throwError(() => err);
        }
        if (err?.error?.['ErrorCode'] === 'inspection_template_delete_unavailable') {
          this.snackbarService.error(SnackbarErrorMessage.RemovingTemplateInUse);
        } else {
          this.snackbarService.error(SnackbarErrorMessage.RemovingTemplate);
        }
        return throwError(() => err);
      }),
      tap(() => {
        this.snackbarService.success(`${template.name} deleted`);
      })
    );
  }

  promoteTemplateToGlobal(templateId: number) {
    return this.restApiService
      .post<number>(`inspections/template/promoteTemplateToGlobal/${templateId}`)
      .pipe(catchError(err => this.handleError(err, SnackbarErrorMessage.UpdatingTemplate)));
  }

  updatePostNonCompletedInstances(templateId: number) {
    return this.restApiService
      .post<number>(`inspections/template/${templateId}/updatePostNonCompletedInstances`)
      .pipe(catchError(err => this.handleError(err, SnackbarErrorMessage.UpdatingTemplate)));
  }

  cloneTemplate(templateId: number) {
    return this.restApiService.post<number>(`inspections/template/${templateId}/clone`).pipe(
      catchError(err => this.handleError(err, SnackbarErrorMessage.CloningTemplate)),
      tap(() => this.templatesCacheService.resetTemplatesCache())
    );
  }

  cloneGlobalTemplate(templateId: number) {
    return this.restApiService
      .post<number>(`inspections/template/global/${templateId}/clone`)
      .pipe(catchError(err => this.handleError(err, SnackbarErrorMessage.CloningTemplate)));
  }

  private handleError(err: HttpErrorResponse, type: SnackbarErrorMessage): Observable<never> {
    if (err?.status === 403) {
      this.snackbarService.error(SnackbarErrorMessage.TemplateEditPermissionRequired);
      return throwError(() => err);
    }
    this.snackbarService.error(type);
    return throwError(() => err);
  }

  addInspectionTemplateCategory(name: string): Observable<RestInspectionTemplateCategoryModel> {
    return this.restApiService.post<RestInspectionTemplateCategoryModel>(`inspectionTemplateCategory`, { name }).pipe(
      catchError(err => {
        this.snackbarService.error(SnackbarErrorMessage.AddInspectionTemplateCategoryError);
        return throwError(() => err);
      })
    );
  }

  editInspectionTemplateCategory(
    category: RestInspectionTemplateCategoryModel
  ): Observable<RestInspectionTemplateCategoryModel> {
    return this.restApiService
      .update<RestInspectionTemplateCategoryModel>(`inspectionTemplateCategory/${category.id}`, category)
      .pipe(
        catchError(err => {
          this.snackbarService.error(SnackbarErrorMessage.InspectionTemplateCategoryUpdateError);
          return throwError(() => err);
        })
      );
  }

  deleteInspectionTemplateCategory(id: number): Observable<void> {
    return this.restApiService.delete<void>(`inspectionTemplateCategory/${id}`).pipe(
      catchError(err => {
        this.snackbarService.error(SnackbarErrorMessage.InspectionTemplateCategoryDeleteError);
        return throwError(() => err);
      })
    );
  }
}
